import { momentZodType } from 'lib/zod';
import { Moment } from 'moment';
import momentTz from 'moment-timezone';
import { guessCurrentTimeZone } from 'utils/datetime';
import { z } from 'zod';
import { VariantFormFieldKey, VariantSendTime } from '../types';
import { convertTimeZone, getDateTimeStampFromSeparateDateAndTime } from '../utils';

type SendTimeRefineArgs = {
  [VariantFormFieldKey.SEND_TIME_DATE]: Moment;
  [VariantFormFieldKey.SEND_TIME_TIME]: string;
  [VariantFormFieldKey.SEND_TIME_VALIDATION_META_INFO]: {
    variantId: number;
    isControl: boolean;
    originalSendTimeZone: string;
    experimentSendTimeZone: string;
    allVariantSendTimes: VariantSendTime[];
  };
};

const TIME_THRESHOLD_BETWEEN_VARIANTS = 60 * 60 * 1000; // 1 hour in milliseconds

const getSelectedStartTime = (args: SendTimeRefineArgs) => {
  const {
    sendTimeDate,
    sendTimeTime,
    sendTimeValidationMetaInfo: { experimentSendTimeZone },
  } = args;
  const userTimeZone = guessCurrentTimeZone();
  const sendTimeDateOffset = sendTimeDate.utcOffset();
  const userTimeZoneOffset = momentTz().tz(userTimeZone).utcOffset();
  const shouldConvert = sendTimeDateOffset !== userTimeZoneOffset;

  /**
   * As the user changes the date using our date picker
   * the resultant date is in the user's timezone. All our send time
   * related code works based on this assumption.
   *
   * The reason why we manually convert out here is because on
   * first load the resulting send time is what we calculate
   * using the experiment's timezone. This will ensure that we
   * are consistently using the user's timezone.
   *
   * Of course, this is only needed if the user is not in the same timezone
   * as the experiment.
   */
  const normalizedDate = shouldConvert
    ? convertTimeZone(sendTimeDate.toISOString(), userTimeZone, experimentSendTimeZone)
    : sendTimeDate;

  const selectedStartTime = getDateTimeStampFromSeparateDateAndTime(
    normalizedDate,
    sendTimeTime,
    experimentSendTimeZone,
  );

  return selectedStartTime;
};

export const isStartTimeInTheFuture = (args: SendTimeRefineArgs) => {
  const {
    sendTimeValidationMetaInfo: { experimentSendTimeZone },
  } = args;
  const currentTimeInTimeZone = momentTz.tz(experimentSendTimeZone);
  const selectedStartTime = getSelectedStartTime(args);

  return selectedStartTime?.isAfter(currentTimeInTimeZone);
};

export const isControlTheEarliestTime = (args: SendTimeRefineArgs) => {
  const {
    sendTimeValidationMetaInfo: {
      originalSendTimeZone,
      variantId,
      isControl,
      allVariantSendTimes,
      experimentSendTimeZone,
    },
  } = args;
  const controlStartTime = allVariantSendTimes?.[0].sendTime ?? '';
  const currentControlStartTime = convertTimeZone(
    controlStartTime,
    experimentSendTimeZone,
    originalSendTimeZone,
  );

  const selectedStartTime = getSelectedStartTime(args);

  // We want to compare the control variant against
  // all other variants to check if it's the earliest
  if (isControl) {
    for (let index = 0; index < allVariantSendTimes.length; index++) {
      const validatingVariant = allVariantSendTimes[index];
      // This check prevents checking the same variant against itself
      if (validatingVariant.id !== variantId && validatingVariant.sendTime) {
        const validatingVariantStartTime = convertTimeZone(
          validatingVariant.sendTime,
          experimentSendTimeZone,
          originalSendTimeZone,
        );
        if (selectedStartTime.isAfter(validatingVariantStartTime)) {
          return false;
        }
      }
    }
    return true;
  }

  return selectedStartTime.isAfter(currentControlStartTime);
};

export const areAllVariantSendTimeAtLeastOneHourApart = (args: SendTimeRefineArgs) => {
  const {
    sendTimeValidationMetaInfo: {
      originalSendTimeZone,
      experimentSendTimeZone,
      variantId,
      allVariantSendTimes,
    },
  } = args;

  const selectedStartTime = getSelectedStartTime(args);

  for (let index = 0; index < allVariantSendTimes.length; index++) {
    const validatingVariant = allVariantSendTimes[index];
    // This check prevents checking the same variant against itself
    if (validatingVariant.id !== variantId && validatingVariant.sendTime) {
      const validatingVariantStartTime = convertTimeZone(
        validatingVariant.sendTime,
        experimentSendTimeZone,
        originalSendTimeZone,
      );
      const diffInMillis = Math.abs(selectedStartTime.diff(validatingVariantStartTime));
      if (diffInMillis < TIME_THRESHOLD_BETWEEN_VARIANTS) {
        return false;
      }
    }
  }

  return true;
};

export const sendTimeSchema = z
  .object({
    [VariantFormFieldKey.SEND_TIME_DATE]: momentZodType,
    [VariantFormFieldKey.SEND_TIME_TIME]: z
      .string()
      .refine(
        (value) => /^((0[1-9]|1[0-2]):[0-5][0-9] [APap][Mm])$/.test(value),
        'sendTimeExperiment.time',
      ),
    [VariantFormFieldKey.SEND_TIME_VALIDATION_META_INFO]: z.object({
      variantId: z.number(),
      isControl: z.boolean(),
      originalSendTimeZone: z.string(),
      experimentSendTimeZone: z.string(),
      allVariantSendTimes: z.array(
        z.object({
          id: z.number(),
          sendTime: z.string().nullable(),
        }),
      ),
    }),
  })
  .refine(isStartTimeInTheFuture, {
    path: [VariantFormFieldKey.SEND_TIME_DATE, VariantFormFieldKey.SEND_TIME_TIME],
    message: 'sendTimeExperiment.futureTime',
  })
  .refine(isControlTheEarliestTime, {
    path: [VariantFormFieldKey.SEND_TIME_DATE, VariantFormFieldKey.SEND_TIME_TIME],
    message: 'sendTimeExperiment.oneHourApartAndAfterStartTime',
  })
  .refine(areAllVariantSendTimeAtLeastOneHourApart, {
    path: [VariantFormFieldKey.SEND_TIME_DATE, VariantFormFieldKey.SEND_TIME_TIME],
    message: 'sendTimeExperiment.oneHourApartAndAfterStartTime',
  });
